import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Billing, Service } from 'webapp-types';
import { BillingServiceType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class BillingServicesComponent extends Component {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('flow/cancellation') declare flowCancellationService: Service.Flow.Cancellation;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	get model() {
		return this.billingService.prepareServicesList().map((item) => {
			let text = 'Contact';
			if (item.isPurchasable) {
				text = 'Buy';
			} else if (item.id === BillingServiceType.RESELLER_PARTNER) {
				text = 'Book a call';
			}

			let isDisabled = false,
				tooltipText = '';

			if (this.billingService.isPaused && item.price && item.isPurchasable) {
				isDisabled = true;
				tooltipText = this.flowCancellationService.disabledTooltipTextForAddonsAndServices;
			}

			let theme = 'blue';
			if (item.id === BillingServiceType.RESELLER_PARTNER) {
				theme = 'blue-outlined';
			}

			return {
				...item,
				text,
				isDisabled,
				tooltipText,
				theme,
			};
		});
	}

	get canRequestSetupHelp(): boolean {
		return !this.currentUserService.isClientUserInPaidClientSpace;
	}

	@action
	choose(option: Billing.ServiceInfo) {
		if (option.isPurchasable) {
			this.billingService.openServicesConfirmPopup(option);
		} else {
			const origin =
				option.id === BillingServiceType.RESELLER_PARTNER
					? 'billing_page_tile'
					: 'billing_page_text';

			this.analyticsService.event(TrackingEvent.CALL_INITIATED, {
				origin,
			});
			window.open(option.bookCallLink, '_blank');
		}
	}

	@action
	learnMore(option: Billing.ServiceInfo) {
		if (option.id === BillingServiceType.RESELLER_PARTNER) {
			this.analyticsService.event(TrackingEvent.RESELLER_PROMO_CLICKED, {
				origin: 'billing_page_tile',
			});
		}

		window.open(option.learnMoreUrl, '_blank');
	}
	<template>
		{{#if this.model}}
			<div class="services">
				<div class="services__header">
					<h1>Services</h1>
					{{#if this.canRequestSetupHelp}}
						<div class="help">
							<span>Need help?</span>
							<a
								role="button"
								href={{this.accountService.requestSetupHelpLink}}
								target="_blank"
							>
								<CommonUiIconComponent @name="Phone" @size="sm" />
								Book a call
							</a>
						</div>
					{{/if}}
				</div>
				<ul>
					{{#each this.model as |item|}}
						<li>
							<div class="header-ct">
								<div class="title">
									<CommonUiIconComponent @name={{item.icon}} @size="lg" />
									<h3>{{item.name}}</h3>
								</div>
							</div>
							<div class="text-ct">
								<p>
									{{{item.description}}}
									{{#if item.learnMoreUrl}}
										<a role="button" {{on "click" (fn this.learnMore item)}}>
											Learn more
										</a>
									{{else if item.bookCallLink}}
										<a role="button" {{on "click" (fn this.choose item)}}>
											Book a call
										</a>
									{{/if}}
								</p>
							</div>
							<div class="action-ct">
								<div class="price">
									{{#if item.price}}
										<strong>{{item.priceFormatted}}</strong>
									{{else if (eq item.priceFormatted "Custom")}}
										<strong>Custom</strong>
									{{else}}
										<strong>Free</strong>
									{{/if}}
								</div>

								<SubmitButtonComponent
									@text={{item.text}}
									@noDelay={{true}}
									@isDisabled={{item.isDisabled}}
									@onTrigger={{fn this.choose item}}
									@tooltipText={{item.tooltipText}}
									@tooltipTheme="light"
									@theme={{item.theme}}
								/>
							</div>
						</li>
					{{/each}}
				</ul>
			</div>
		{{/if}}
	</template>
}
